const UTM_PARAM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_id", "utm_content", "utm_term"];
const UTM_STORAGE_PREFIX = "acc_enrolment_";

export const SCHOOL_CODE = "ACCMARSDEN";
export const PARENT_LOCATION = "ACC Marsden Park";
export const DEFAULT_ENROLMENT_TYPE = "On Campus";

export const ENROLMENT_TYPES = ["On Campus", "Distance Education"];

export const YEAR_LEVELS = [
  "Kindergarten",
  "Prep",
  "Year 1",
  "Year 2",
  "Year 3",
  "Year 4",
  "Year 5",
  "Year 6",
  "Year 7",
  "Year 8",
  "Year 9",
  "Year 10",
  "Year 11",
  "Year 12"
];

export const INTENDED_START_OPTIONS = [
  "Term 1",
  "Term 2",
  "Term 3",
  "Term 4",
  "Immediate",
  "Start of year"
];

export const HEAR_ABOUT_US_OPTIONS = [
  "Other",
  "Google search",
  "Inflight magazine",
  "Friend/Relative",
  "Instagram advertising",
  "Facebook advertising",
  "Media coverage",
  "Driving past",
  "Billboard",
  "Koorong"
];

export const ENTRY_YEARS = Array.from({length: 21}, (_, index) => 2026 + index);

export const COUNTRY_DIAL_OPTIONS = [
  {iso: "AU", name: "Australia", dial: "61"},
  {iso: "NZ", name: "New Zealand", dial: "64"},
  {iso: "GB", name: "United Kingdom", dial: "44"},
  {iso: "US", name: "United States", dial: "1"},
  {iso: "CA", name: "Canada", dial: "1"},
  {iso: "CN", name: "China", dial: "86"},
  {iso: "IN", name: "India", dial: "91"},
  {iso: "PH", name: "Philippines", dial: "63"},
  {iso: "ID", name: "Indonesia", dial: "62"},
  {iso: "MY", name: "Malaysia", dial: "60"},
  {iso: "SG", name: "Singapore", dial: "65"},
  {iso: "HK", name: "Hong Kong", dial: "852"},
  {iso: "KR", name: "South Korea", dial: "82"},
  {iso: "JP", name: "Japan", dial: "81"},
  {iso: "ZA", name: "South Africa", dial: "27"},
  {iso: "AE", name: "United Arab Emirates", dial: "971"},
  {iso: "IE", name: "Ireland", dial: "353"},
  {iso: "DE", name: "Germany", dial: "49"},
  {iso: "FR", name: "France", dial: "33"},
  {iso: "IT", name: "Italy", dial: "39"},
  {iso: "NL", name: "Netherlands", dial: "31"},
  {iso: "PG", name: "Papua New Guinea", dial: "675"},
  {iso: "FJ", name: "Fiji", dial: "679"},
  {iso: "WS", name: "Samoa", dial: "685"},
  {iso: "TO", name: "Tonga", dial: "676"},
  {iso: "VN", name: "Vietnam", dial: "84"},
  {iso: "TH", name: "Thailand", dial: "66"},
  {iso: "PK", name: "Pakistan", dial: "92"},
  {iso: "BD", name: "Bangladesh", dial: "880"},
  {iso: "LK", name: "Sri Lanka", dial: "94"},
  {iso: "NP", name: "Nepal", dial: "977"},
  {iso: "BR", name: "Brazil", dial: "55"},
  {iso: "MX", name: "Mexico", dial: "52"}
];

// Guest Apex REST on the Salesforce Experience Cloud site. Do not use
// *.my.salesforce-sites.com or cemacc--cemfc.sandbox.my.site.com on acc.edu.au.
export const PRODUCTION_ENROLMENT_INTAKE_URL =
  "https://cemacc.my.site.com/lpvforcesite/services/apexrest/cem/enrolment/intake";

function isDisallowedIntakeHost(url) {
  return /salesforce-sites\.com/i.test(url) || /cemacc--cemfc\.sandbox/i.test(url);
}

export function getEnrolmentIntakeUrl() {
  const configured = (process.env.GATSBY_ENROLMENT_INTAKE_URL || "").trim().replace(/\/$/, "");
  if (!configured || isDisallowedIntakeHost(configured)) {
    return PRODUCTION_ENROLMENT_INTAKE_URL;
  }
  return configured;
}

export function createSubmissionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

export function getLandingPage() {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}${window.location.pathname}`;
}

export function persistUtmFromLocation(location) {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") return;
  const search = (location && location.search) || window.location.search || "";
  const params = new URLSearchParams(search);
  UTM_PARAM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      sessionStorage.setItem(`${UTM_STORAGE_PREFIX}${key}`, value);
    }
  });
}

export function getStoredUtm() {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") return undefined;
  const utm = {};
  UTM_PARAM_KEYS.forEach((key) => {
    const value = sessionStorage.getItem(`${UTM_STORAGE_PREFIX}${key}`);
    if (value) {
      utm[key.replace(/^utm_/, "")] = value;
    }
  });
  return Object.keys(utm).length ? utm : undefined;
}

export function clearStoredUtm() {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") return;
  UTM_PARAM_KEYS.forEach((key) => {
    sessionStorage.removeItem(`${UTM_STORAGE_PREFIX}${key}`);
  });
}

export function buildContext({channel, sourceForm, schoolCode, extra} = {}) {
  const context = {
    channel,
    sourceForm,
    externalSubmissionId: createSubmissionId(),
    landingPage: getLandingPage()
  };
  if (schoolCode) context.schoolCode = schoolCode;
  const utm = getStoredUtm();
  if (utm) context.utm = utm;
  return extra ? {...context, ...extra} : context;
}

export function toE164(countryIso, rawNumber) {
  const country = COUNTRY_DIAL_OPTIONS.find((option) => option.iso === countryIso) || COUNTRY_DIAL_OPTIONS[0];
  const trimmed = (rawNumber || "").trim();
  if (!trimmed) return "";

  if (trimmed.startsWith("+")) {
    return `+${trimmed.replace(/[^\d]/g, "")}`;
  }

  let digits = trimmed.replace(/[^\d]/g, "");
  if (digits.startsWith(country.dial) && digits.length > country.dial.length + 5) {
    return `+${digits}`;
  }
  if (digits.startsWith("0")) {
    digits = digits.replace(/^0+/, "");
  }
  return digits ? `+${country.dial}${digits}` : "";
}

export function isValidE164(value) {
  return /^\+[1-9]\d{7,14}$/.test(value || "");
}

export function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());
}

export function isValidDob(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return false;
  const [year, month, day] = value.split("-").map(Number);
  if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date > today) return false;
  if (year < 1990) return false;
  return true;
}

export function todayIsoDate() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function corsHint(error) {
  if (typeof window === "undefined") return "";
  const isLocalhost = /localhost|127\.0\.0\.1/.test(window.location.hostname);
  const failedFetch = error && (error.message === "Failed to fetch" || error.name === "TypeError");
  if (isLocalhost && failedFetch) {
    return ". Salesforce guest intake CORS allows https://www.acc.edu.au and https://acc.edu.au only. Local requests cannot reach production.";
  }
  return "";
}

const INTAKE_FETCH_OPTIONS = {
  credentials: "omit",
  headers: {Accept: "application/json"}
};

async function readJson(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (error) {
    return {error: text};
  }
}

function stripSalesforceIds(payload) {
  if (!payload || typeof payload !== "object") return payload;
  const rest = {...payload};
  delete rest.caseId;
  delete rest.opportunityIds;
  delete rest.submissionId;
  return rest;
}

export function messageFromSalesforce(payload, fallback) {
  const data = stripSalesforceIds(payload) || {};
  return data.blockedMessage || data.error || data.message || fallback;
}

export function intakeMatchesChild(intake, child) {
  if (!intake || !child) return false;
  if (intake.yearLevel && child.yearLevel && intake.yearLevel !== child.yearLevel) return false;
  if (intake.enrolmentType && child.enrolmentType && intake.enrolmentType !== child.enrolmentType) return false;
  const childEntry = child.entryYear === "" || child.entryYear == null ? null : Number(child.entryYear);
  if (intake.entryYear != null && childEntry != null && Number(intake.entryYear) !== childEntry) return false;
  if (intake.yearLevel && !child.yearLevel) return false;
  if (intake.enrolmentType && !child.enrolmentType) return false;
  if (intake.entryYear != null && childEntry == null) return false;
  return Boolean(child.yearLevel || child.enrolmentType || childEntry != null);
}

export function getIntakeBlock(intakes, flag, children = []) {
  const blockedRows = Array.isArray(intakes) ? intakes.filter((row) => row && row[flag]) : [];
  const matching = blockedRows.filter((row) => children.some((child) => intakeMatchesChild(row, child)));
  const messages = matching.map((row) => row.message).filter(Boolean);
  if (matching.length > 0 && messages.length === 0) {
    messages.push("This form is currently closed for the selected year level.");
  }
  return {
    isBlocked: matching.length > 0,
    messages
  };
}

export async function fetchIntakes(schoolCode = SCHOOL_CODE) {
  const baseUrl = getEnrolmentIntakeUrl();
  if (!baseUrl) {
    throw new Error("Enrolment intake URL is not configured.");
  }
  const url = `${baseUrl}?resource=intakes&schoolCode=${encodeURIComponent(schoolCode)}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      ...INTAKE_FETCH_OPTIONS
    });
    const data = await readJson(response);
    if (!response.ok) {
      throw new Error(messageFromSalesforce(data, `Could not load intake status (${response.status}).`));
    }
    return {
      schoolCode: data.schoolCode || schoolCode,
      intakes: Array.isArray(data.intakes) ? data.intakes : []
    };
  } catch (error) {
    error.message = `${error.message || "Could not load intake status."}${corsHint(error)}`;
    throw error;
  }
}

export async function fetchIntakesOrEmpty(schoolCode = SCHOOL_CODE) {
  try {
    const data = await fetchIntakes(schoolCode);
    return Array.isArray(data.intakes) ? data.intakes : [];
  } catch (error) {
    return [];
  }
}

export function normalizeTourSlots(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.slots)) return data.slots;
  if (data && Array.isArray(data.tours)) return data.tours;
  return [];
}

export async function fetchTourSlots({schoolCode = SCHOOL_CODE, partySize}) {
  const baseUrl = getEnrolmentIntakeUrl();
  if (!baseUrl) {
    throw new Error("Enrolment intake URL is not configured.");
  }
  const size = Math.max(1, parseInt(partySize, 10) || 1);
  const url = `${baseUrl}?schoolCode=${encodeURIComponent(schoolCode)}&partySize=${encodeURIComponent(size)}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      ...INTAKE_FETCH_OPTIONS
    });
    const data = await readJson(response);
    if (!response.ok) {
      throw new Error(messageFromSalesforce(data, `Could not load tour times (${response.status}).`));
    }
    return normalizeTourSlots(data);
  } catch (error) {
    error.message = `${error.message || "Could not load tour times."}${corsHint(error)}`;
    throw error;
  }
}

export async function postEnrolmentIntake(payload) {
  const baseUrl = getEnrolmentIntakeUrl();
  if (!baseUrl) {
    throw new Error("Enrolment intake URL is not configured.");
  }
  try {
    const response = await fetch(baseUrl, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify(payload)
    });
    const data = await readJson(response);
    return {response, data};
  } catch (error) {
    error.message = `${error.message || "Could not submit the form."}${corsHint(error)}`;
    throw error;
  }
}

export function formatTourSlotLabel(slot) {
  const dateLabel = formatTourDate(slot.tourDate);
  const parts = [slot.name, dateLabel, slot.tourTime].filter(Boolean);
  let label = parts.join(" — ");
  if (slot.remaining != null && slot.remaining !== "") {
    const remaining = Number(slot.remaining);
    if (!Number.isNaN(remaining)) {
      label += remaining === 1 ? " (1 place left)" : ` (${remaining} places left)`;
    }
  }
  return label;
}

function formatTourDate(value) {
  if (!value) return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric"
  });
}
